<template>
  <div class="p-5 flex flex-col">
    <p class="font-semibold text-3xl">
      {{ this.$route.meta.title }}
    </p>
    <vs-row
      vs-w="12"
      class="mt-5 filter-container"
    >
      <vs-col vs-w="1">
        <p class="font-semibold">
          Filter
        </p>
      </vs-col>
      <vs-col vs-w="6" />
      <vs-col vs-w="2">
        <date-picker
          class="px-2"
          type="date"
          format="DD-MMM-YYYY"
          value-type="format"
          placeholder="Start Date"
          v-model="filters.start_date"
          :custom-disabled-date="disabledStartDate"
        />
      </vs-col>
      <vs-col vs-w="2">
        <date-picker
          class="px-2 "
          type="date"
          format="DD-MMM-YYYY"
          value-type="format"
          placeholder="End Date"
          v-model="filters.end_date"
          :custom-disabled-date="disabledEndDate"
        />
      </vs-col>
      <vs-col
        vs-w="1"
        class="flex flex-col justify-center"
      >
        <vs-button
          type="border"
          color="primary"
          class="self-center font-semibold"
          style="min-width: 100px;"
          @click="resetFilters"
        >
          Reset
        </vs-button>
      </vs-col>
    </vs-row>
    <div class="p-5 flex flex-col mt-5 bg-white rounded-md">
      <table-top
        hide-search
        :page-size-start="false"
        class="mt-6"
        @fetch="fetchExchangeRates()"
      >
        <template #start>
          <div
            class="flex items-center justify-center"
          >
            <vs-button
              v-show="isUserCanAccessMenu(permission.name, 'add')"
              class="button w-max font-semibold"
              color="primary"
              @click="startAdd"
            >
              Add New Exchange Rate
            </vs-button>
            <vs-button
              class="button w-max font-semibold ml-5"
              color="secondary"
              @click="isDownload = true"
            >
              Export
            </vs-button>
          </div>
        </template>
      </table-top>
      <data-table
        class="mt-6"
        :table-header="exchangeRateHeaders"
        :table-data="exchangeRateData"
        :sort-by="filters.sort_by"
        :sort-dir="filters.sort_dir"
        body-height="auto"
        :frozen-columns="isActionColumnVisible ? 5 : 4"
        :loading="isBusy"
        @sort="sort"
      >
        <template #no="{ index }">
          <div class="flex flex-row">
            {{ (currentPage - 1) * pageSize + index + 1 }}
          </div>
        </template>
        <template #action="{ row }">
          <div class="flex flex-row">
            <template v-if="row.isEditing">
              <div
                :class="`mx-1 self-center flex items-center justify-center icon-transparent-bg complete ${isProcessing && 'cursor-not-allowed opacity-50 animate-bounce'}`"
                @click="isProcessing ? null : saveEdit(row)"
              >
                <img
                  :src="require('@/assets/shipo-icon-check-circle.svg')"
                  alt="save"
                >
              </div>
              <div
                :class="`mx-1 self-center flex items-center justify-center icon-transparent-bg delete ${isProcessing && 'cursor-not-allowed opacity-50 animate-bounce'}`"
                @click="isProcessing ? null : cancelEdit(row)"
              >
                <img
                  :src="require('@/assets/shipo-icon-close-circle.svg')"
                  alt="cancel"
                >
              </div>
            </template>
            <template v-else>
              <div
                v-if="isUserCanAccessMenu(permission.name, 'edit')"
                class="mx-1 self-center flex items-center justify-center icon-transparent-bg"
                @click="startEdit(row)"
              >
                <img
                  :src="require('@/assets/shipo-icon-edit.svg')"
                  alt="edit"
                >
              </div>
            </template>
          </div>
        </template>
        <template #date="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <div>
                <date-picker
                  v-model="row.editForm.date.value"
                  style="min-width: 200px;"
                  type="date"
                  value-type="YYYY-MM-DD"
                  format="DD-MMM-YYYY"
                  :class="{ 'is-not-valid': !row.editForm.date.isValid }"
                  placeholder="Date"
                  :disabled="isProcessing"
                  :max="moment().format('YYYY-MM-DD')"
                />
              </div>
              <span
                v-if="!row.editForm.date.value"
                class="text-danger text-sm absolute-bottom-5 left-0"
              >*Required</span>
            </div>
          </template>
          <template v-else>
            {{ row.date ? moment(row.date).format('DD-MMM-YYYY') : '-' }}
          </template>
        </template>
        <template #usd_to_idr="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <div>
                <autonumeric
                  v-model="row.editForm.usd_to_idr.value"
                  :class="{ 'is-not-valid': !row.editForm.usd_to_idr.isValid }"
                  class="w-56"
                  placeholder="Enter USD to IDR rate"
                  :disabled="isProcessing"
                  :options="globalNumericOptions"
                />
              </div>
              <span
                v-if="!row.editForm.usd_to_idr.value"
                class="text-danger text-sm absolute -bottom-5 left-0"
              >* Required</span>
            </div>
          </template>
          <template v-else>
            {{ `Rp ${row.usd_to_idr ? formatRupiah(row.usd_to_idr) : '-'}` }}
          </template>
        </template>
      </data-table>
      <table-bottom
        class="mt-6"
        @fetch="fetchExchangeRates"
      />
    </div>
    <pop-up
      :active.sync="popUp.isShown"
      :is-warning="true"
      title="Duplicate Date Found"
      left-button="Cancel"
      right-button="Replace"
      :is-busy="isProcessing"
      @close="popUp = { isShown: false, tempId: null, tempName: '', duplicateData: null, isAdding: false, currentRowId: null }"
      @leftButton="popUp = { isShown: false, tempId: null, tempName: '', duplicateData: null, isAdding: false, currentRowId: null }"
      @rightButton="replaceDuplicateExchangeRate()"
    >
      <p class="text-center mt-1">
        An exchange rate already exists for "{{ popUp.tempName }}".
      </p>
      <p class="text-center">
        Do you want to replace it with the new rate?
      </p>
    </pop-up>
  </div>
</template>

<script>

import moment from "moment";
import {mapGetters} from "vuex";

import Autonumeric from "@/components/autonumeric";
import DataTable from "@/components/dataTable";
import DatePicker from "@/components/datePicker";
import PopUp from "@/components/popUp";
import TableBottom from "@/components/tableBottom";
import TableTop from "@/components/tableTop";
import { PAGE_TITLE } from "@/fixtures/Constants";
import downloadUtils from "@/utils/downloadUtils";

export default {
  metaInfo() {
    return {
      title: `${PAGE_TITLE} ${this.$route.meta.title}`
    }
  },
  components: {
    TableTop,
    TableBottom,
    DataTable,
    DatePicker,
    Autonumeric,
    PopUp
  },
  data() {
    return {
      exchangeRateHeaders: [
        { key: 'no', label: 'No', sortable: false, width: '20px' },
        { key: 'date', label: 'Date', sortable: true },
        { key: 'usd_to_idr', label: 'USD to IDR Rate', sortable: true }
      ],
      exchangeRateData: [],
      globalNumericOptions: {
        digitGroupSeparator: ',',
        decimalCharacter: '.',
        decimalPlaces: 2,
        minimumValue: '0',
        allowDecimalPadding: true
      },
      filters: {
        sort_by: 'date',
        sort_dir: 'desc',
        start_date: '',
        end_date: ''
      },
      isAdding: false,
      isDownload: false,
      popUp: {
        isShown: false,
        tempId: null,
        tempName: '',
        duplicateData: null,
        isAdding: false,
        currentRowId: null
      },
      isBusy: false,
      isProcessing: false
    }
  },
  computed: {
    ...mapGetters({
      currentPage: 'Table/currentPage',
      pageSize: 'Table/pageSize',
      exchangeRates: 'ExchangeRates/exchangeRates',
      exchangeRatesDownload: 'ExchangeRates/exchangeRatesDownload'
    }),
    permission() {
      return { page: this.$route.meta.permission.page, name: this.$route.meta.permission.name }
    },
    isActionColumnVisible() {
      return this.isUserCanAccessMenu(this.permission.name, 'edit') || this.isUserCanAccessMenu(this.permission.name, 'delete')
    }
  },
  mounted() {
    this.fetchExchangeRates()
  },
  watch: {
    exchangeRates: {
      handler(val) {
        if (val) {
          this.exchangeRateData = val.map(item => ({
            ...item,
            isEditing: false,
            editForm: null
          }))
        }
      }
    },
    isActionColumnVisible: {
      immediate: true,
      handler(val) {
        const index = this.exchangeRateHeaders.findIndex(header => header.key === 'action');
        if (val && index === -1) {
          this.exchangeRateHeaders.splice(1, 0, { key: 'action', label: 'Action', sortable: false, width: '75px' });
        } else if (!val && index !== -1) {
          this.exchangeRateHeaders.splice(index, 1);
        }
      }
    },
    filters: {
      deep: true,
      handler() {
        this.fetchExchangeRates()
      }
    },
    exchangeRatesDownload: {
      handler(val) {
        if (!this.isEmpty(val)) {
          downloadUtils(val, `ListExchangeRates.xlsx`)
          this.$store.dispatch('ExchangeRates/resetExchangeRatesDownload');
        }
      }
    },
    isDownload: {
      handler(val) {
        if (!!val) {
          this.downloadExchangeRates();
        }
      }
    }
  },
  methods: {
    disabledStartDate(date) {
      if (this.filters.end_date) {
        try {
          const endDate = moment(this.filters.end_date, 'DD-MMM-YYYY').startOf('day')
          return date > endDate.toDate()
        } catch (e) {
          return false
        }
      }
      return false
    },
    disabledEndDate(date) {
      if (this.filters.start_date) {
        try {
          const startDate = moment(this.filters.start_date, 'DD-MMM-YYYY').startOf('day')
          return date < startDate.toDate()
        } catch (e) {
          return false
        }
      }
      return false
    },
    async fetchExchangeRates() {
      try {
        this.isBusy = true
        this.isAdding = false;
        const apiFilters = Object.keys(this.filters)
          .filter(key => !!this.filters[key])
          .reduce((item, key) => {
            item[key] = this.filters[key]
            return item
          }, {})
        await this.$store.dispatch(
          'ExchangeRates/fetchExchangeRates',
          {
            ...apiFilters
          }
        )
        this.isBusy = false
      } catch (e) {
        this.isBusy = false
        this.showFailedNotification(this, 'Failed to fetch exchange rate list, please try again')
      } finally {
        this.isBusy = false
      }
    },
    sort(sortBy, sortDir) {
      this.filters.sort_by = sortBy
      this.filters.sort_dir = sortDir
      this.fetchExchangeRates()
    },
    startAdd() {
      if (this.isAdding || this.exchangeRateData.some(row => row.isEditing)) {
        this.showFailedNotification(
          this,
          'Only one action can be active at a time. \n' + 'Please save your changes first.',
          'Error'
        )
        return
      }

      const newRow = {
        id: `new-${Date.now()}`,
        isEditing: true,
        active: true,
        editForm: {
          date: { value: '', isValid: true },
          usd_to_idr: { value: '', isValid: true }
        }
      }

      this.isAdding = true
      this.exchangeRateData.unshift(newRow)
    },
    startEdit(row) {
      if (this.isAdding || this.exchangeRateData.some(r => r.isEditing)) {
        this.showFailedNotification(
          this,
          'Only one action can be active at a time. \n' + 'Please save your changes first.',
          'Error'
        )
        return
      }

      const index = this.exchangeRateData.findIndex(item => item.id === row.id)
      if (index !== -1) {
        this.$set(this.exchangeRateData[index], 'isEditing', true)
        this.$set(this.exchangeRateData[index], 'editForm', {
          date: { value: row.date, isValid: true },
          usd_to_idr: { value: row.usd_to_idr, isValid: true }
        })
      }
    },
    cancelAllEdits() {
      this.exchangeRateData.forEach(row => {
        if (row.isEditing) {
          this.$set(row, 'isEditing', false)
          this.$set(row, 'editForm', null)
        }
      })
      this.isAdding = false
    },
    cancelEdit(row) {
      if (this.isAdding) {        const index = this.exchangeRateData.findIndex(item => item.id === row.id)
        if (index !== -1) {
          this.exchangeRateData.splice(index, 1)
        }
      } else {
        const index = this.exchangeRateData.findIndex(item => item.id === row.id)
        if (index !== -1) {
          const originalData = this.exchangeRates.find(item => item.id === row.id)
          if (originalData) {
            this.$set(this.exchangeRateData[index], 'isEditing', false)
            this.$set(this.exchangeRateData[index], 'editForm', null)
            this.$set(this.exchangeRateData[index], 'date', originalData.date)
            this.$set(this.exchangeRateData[index], 'usd_to_idr', originalData.usd_to_idr)
          }
        }
      }
      this.isAdding = false
    },
    async saveEdit(row) {
      if (this.validateEditForm(row.editForm)) {
        this.showFailedNotification(
          this,
          'Some fields is empty, please fill it first',
          'Failed'
        )
        return
      }
      try {
        const formData = {
          date: row.editForm.date.value,
          usd_to_idr: row.editForm.usd_to_idr.value
        }

        const dateFilter = moment(formData.date).format('DD-MMM-YYYY')
        let sameDayExchangeRates
        try {
          sameDayExchangeRates = await this.$store.dispatch('ExchangeRates/fetchExchangeRatesForDuplicateCheck', {
            start_date: dateFilter,
            end_date: dateFilter
          })
        } catch (e) {
          this.showFailedNotification(
            this,
            e?.message || 'Failed to verify duplicate date, please try again',
            'Error'
          )
          return
        }
        const existingRecord = sameDayExchangeRates.find(item =>
          this.isAdding || item.id !== row.id
        )
        
        if (existingRecord) {
          this.popUp = {
            isShown: true,
            tempId: existingRecord.id,
            tempName: moment(formData.date).format('DD MMM YYYY'),
            duplicateData: formData,
            isAdding: this.isAdding,
            currentRowId: this.isAdding ? null : row.id
          }
          return
        }
        this.isProcessing = true
        if (this.isAdding) {
          await this.$store.dispatch('ExchangeRates/postExchangeRate', { form: formData })
          this.showSuccessNotification(this, 'Exchange rate successfully added')
        } else {
          await this.$store.dispatch('ExchangeRates/putExchangeRate', { id: row.id, form: formData })
          this.showSuccessNotification(this, 'Exchange rate successfully updated')
        }
        this.cancelAllEdits()
        this.isProcessing = false
        await this.fetchExchangeRates()
      } catch ({ error }) {
        this.isProcessing = false
        this.showFailedNotification(this, error?.errors || 'Failed to save changes, please try again', 'Error')
      }
    },
    validateEditForm(form) {
      Object.keys(form).forEach(key => {
        if (form[key].hasOwnProperty('isValid')) {
          form[key].isValid = !!form[key].value
        }
      })

      return Object.keys(form).some(key =>
        form[key].hasOwnProperty('isValid')
          ? !form[key].isValid
          : false
      )
    },
    formatRupiah(value) {
      return new Intl.NumberFormat('en-US', { 
        minimumFractionDigits: 2, 
        maximumFractionDigits: 2 
      }).format(value)
    },
    async replaceDuplicateExchangeRate() {
      try {
        this.isProcessing = true
        const duplicateData = this.popUp.duplicateData
        await this.$store.dispatch('ExchangeRates/putExchangeRate', {
          id: this.popUp.tempId,
          form: { ...duplicateData, update_on_duplicate: true }
        })
        this.cancelAllEdits()
        await this.fetchExchangeRates()
        this.showSuccessNotification(this, 'Exchange rate replaced successfully')
        this.popUp = { isShown: false, tempId: null, tempName: '', duplicateData: null, isAdding: false, currentRowId: null }
        this.isProcessing = false
      } catch ({ error }) {
        this.isProcessing = false
        this.showFailedNotification(this, error?.errors || 'Failed to replace exchange rate, please try again', 'Error')
      }
    },
    resetFilters() {
      this.filters = {
        sort_by: '',
        sort_dir: '',
        start_date: '',
        end_date: ''
      };

      this.fetchExchangeRates()
    },
    async downloadExchangeRates() {
      try {
        this.isBusy = true
        const payload = {
          sort_by: this.filters.sort_by || 'date',
          sort_dir: this.filters.sort_dir || 'desc'
        }

        if (this.filters.start_date) {
          payload.start_date = this.filters.start_date
        }
        if (this.filters.end_date) {
          payload.end_date = this.filters.end_date
        }

        await this.$store.dispatch('ExchangeRates/downloadExchangeRates', payload, {
          headers: {
            accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          },
          responseType: 'blob'
        });
        this.isBusy = false
        this.isDownload = false;
      } catch (error) {
        this.isBusy = false
        this.isDownload = false
        this.showFailedNotification(this, 'Failed to download Exchange Rate, please try again')
      }
    }
  },
  beforeCreate() {
    this.$store.dispatch('Table/resetConfig')
  }
}
</script>

<style lang="scss" scoped>
/deep/.vs-textarea {
  min-height: 20px;
  height: calc(1rem * 10 / 3);
  resize: none;
}

.filter-container {
  @apply p-5 mt-5 flex flex-row items-center bg-white rounded-lg;
}

.icon-transparent-bg {
  @apply cursor-pointer;
  @apply min-w-8;
  @apply h-8;
  background-color: #493493;
  border-radius: 0.5rem;

  &.complete {
    background-color: #D1F9E3;
  }

  &.delete {
    background-color: rgba(255, 99, 123, 0.16);
  }
}

.is-not-valid {
  /deep/.vs-con-input,
  /deep/.mx-input-wrapper,
  /deep/.bg-accent-multiselect {
    @apply rounded-lg;
    outline: 2px solid #ff647c;
  }
}

.button-l-plan {
  @apply flex;
  @apply items-center;
  @apply justify-center;
  padding: 0.15rem 0.25rem;
  border-radius: 5px;
}

.btn-download {
  background-color: rgba(76, 175, 80, 0.2);
  color: #4CAF50;
}

.badge-status {
  background-color: #4CAF5033;
  color: #4CAF50;
  text-align: center;
  padding: 5px 0;
  border-radius: 5px;

  &.inactive {
    background-color: rgba(100, 100, 100, 0.2);
    color: #646464
  }
}
</style>